import React from 'react';
import { useParams } from 'react-router-dom';
import { AuthContext, AuthContextType, ServicesContext, usePatientService } from '~/services';

export function useRoleActions(access: string, patient?: PatientType | null): boolean {
    const { authenticated } = React.useContext(AuthContext) as AuthContextType;
    const context = React.useContext(ServicesContext);
    const { getPatient } = usePatientService();
    const { id } = useParams<{ id?: string }>();

    return React.useMemo(() => {
        if (!access) return true;
        if (!authenticated) return false;
        let roles;
        if (context.user.isMACLUser()) {
            // If they're an MACL user, check their access that way
            const maclId = !patient?.macls?.[0]?.id
                ? getPatient(Number(id))?.macls?.[0]?.id
                : patient.macls[0].id;
            if (!maclId) return false;
            roles = context.user.get().macl_users?.find((x: MaclUserType) => x.macl.id === maclId);
        } else if (context.user.isMarketUser()) {
            // And market users
            const marketId = !patient?.markets?.[0]?.id
                ? getPatient(Number(id))?.markets?.[0]?.id
                : patient.markets[0].id;
            if (!marketId) return false;
            roles = context.user
                .get()
                .market_users?.find((x: MarketUserType) => x.market.id === marketId);
        } else {
            // Finally org users
            // if no patient in props, get stored object via the url
            // We always use organization 0 for these checks, backend ensures
            // that permissions are ordered correctly in this array
            const patientOrgId = !patient?.organizations?.[0]?.id
                ? getPatient(Number(id))?.organizations?.[0]?.id
                : patient.organizations[0].id;
            if (!patientOrgId) return false;
            roles = context.user
                .get()
                .organization_users?.find((x: OrgUserType) => x.organization.id === patientOrgId);
        }

        if (!roles?.access_policy) return false;
        return roles.access_policy[access];
    }, [access, authenticated, getPatient, context.user, id, patient]);
}
